import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, CheckCircle2, Pencil, Trash2, Reply, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[#E2DACB] px-3 py-2.5 text-sm text-[#233D35] focus:border-[#C58A47] focus:outline-none transition-colors";

const btnBase = "inline-flex items-center gap-1.5 font-head font-bold uppercase tracking-[0.1em] text-xs px-3.5 py-2 transition-colors";

const QuoteCard = ({ q, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [responding, setResponding] = useState(false);
  const [draft, setDraft] = useState({ ...q });
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const responded = q.status === "responded";

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/quotes/${q.id}`, draft, { withCredentials: true });
      toast.success("Enquiry updated.");
      setEditing(false);
      await onChange();
    } catch {
      toast.error("Couldn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the enquiry from ${q.name}? This can't be undone.`)) return;
    await axios.delete(`${API}/admin/quotes/${q.id}`, { withCredentials: true });
    toast.success("Enquiry deleted.");
    await onChange();
  };

  const toggleResponded = async () => {
    await axios.put(`${API}/admin/quotes/${q.id}`, { status: responded ? "new" : "responded" }, { withCredentials: true });
    await onChange();
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/quotes/${q.id}/respond`, { message: reply }, { withCredentials: true });
      toast.success(`Reply sent to ${q.email}.`);
      setResponding(false);
      setReply("");
      await onChange();
    } catch {
      toast.error("Couldn't send the reply. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E2DACB] p-5 sm:p-6" data-testid={`quote-item-${q.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-head font-bold uppercase tracking-tight text-xl text-[#233D35]">{q.name}</span>
            {q.business && <span className="text-sm text-[#314F64]">· {q.business}</span>}
            <span
              className={`inline-flex items-center gap-1.5 font-head font-semibold uppercase tracking-[0.14em] text-[10px] px-2 py-1 ${
                responded ? "bg-[#233D35] text-[#F3EFE5]" : "bg-[#C58A47] text-[#F3EFE5]"
              }`}
              data-testid={`quote-status-${q.id}`}
            >
              {responded ? <><Check size={12} /> Responded</> : "Not responded yet"}
            </span>
          </div>
          <div className="text-sm text-[#77786F] mt-1">
            <a href={`mailto:${q.email}`} className="text-[#C58A47] hover:underline">{q.email}</a>
            {q.phone && <span> · {q.phone}</span>}
          </div>
        </div>
        <div className="text-right">
          {q.estimate && <span className="block font-head font-bold text-lg text-[#233D35]">{q.estimate}</span>}
          {q.service_type && <span className="block text-xs text-[#77786F]">{q.service_type}</span>}
          <span className="block text-xs text-[#77786F]/70 mt-1">{new Date(q.created_at).toLocaleString()}</span>
        </div>
      </div>

      <p className="mt-3 text-sm text-[#233D35]/90 leading-relaxed border-t border-[#E2DACB] pt-3">{q.message}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => { setResponding(!responding); setEditing(false); }} data-testid={`quote-respond-${q.id}`}
          className={`${btnBase} bg-[#C58A47] text-[#F3EFE5] hover:bg-[#B0793A]`}>
          <Reply size={14} /> Respond
        </button>
        <button onClick={() => { setEditing(!editing); setResponding(false); setDraft({ ...q }); }} data-testid={`quote-edit-${q.id}`}
          className={`${btnBase} border border-[#233D35] text-[#233D35] hover:bg-[#233D35] hover:text-[#F3EFE5]`}>
          <Pencil size={14} /> Edit
        </button>
        <button onClick={toggleResponded} data-testid={`quote-toggle-${q.id}`}
          title={responded ? "Mark as not responded" : "Mark as responded"}
          className={`${btnBase} border ${responded ? "border-[#C58A47] text-[#C58A47]" : "border-[#233D35] text-[#233D35]"} hover:opacity-80`}>
          <CheckCircle2 size={14} /> {responded ? "Mark Not Responded" : "Mark Responded"}
        </button>
        <button onClick={remove} data-testid={`quote-delete-${q.id}`}
          className={`${btnBase} border border-red-300 text-red-700 hover:bg-red-50`}>
          <Trash2 size={14} /> Delete
        </button>
      </div>

      {responding && (
        <div className="mt-4 border-t border-[#E2DACB] pt-4" data-testid={`quote-respond-form-${q.id}`}>
          <label className="block text-sm font-semibold text-[#233D35] mb-1.5">Reply to {q.email}</label>
          <textarea rows={4} className={inputCls} value={reply} onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply — it arrives as an email from Good Work Web Co., and their reply comes back to you."
            data-testid={`quote-reply-text-${q.id}`} />
          <div className="mt-3 flex gap-2">
            <button onClick={sendReply} disabled={busy || !reply.trim()} data-testid={`quote-reply-send-${q.id}`}
              className={`${btnBase} bg-[#233D35] text-[#F3EFE5] hover:bg-[#1A2621] disabled:opacity-60`}>
              <Send size={14} /> {busy ? "Sending…" : "Send Reply"}
            </button>
            <button onClick={() => setResponding(false)} className={`${btnBase} border border-[#E2DACB] text-[#77786F]`} data-testid={`quote-reply-cancel-${q.id}`}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="mt-4 border-t border-[#E2DACB] pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`quote-edit-form-${q.id}`}>
          {[["name", "Name"], ["business", "Business"], ["email", "Email"], ["phone", "Phone"], ["service_type", "Package"], ["estimate", "Ballpark"]].map(([key, label]) => (
            <div key={key}>
              <label className="block text-xs font-semibold text-[#233D35] mb-1">{label}</label>
              <input className={inputCls} value={draft[key] || ""} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                data-testid={`quote-edit-${key}-${q.id}`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-[#233D35] mb-1">Message</label>
            <textarea rows={3} className={inputCls} value={draft.message || ""} onChange={(e) => setDraft({ ...draft, message: e.target.value })}
              data-testid={`quote-edit-message-${q.id}`} />
          </div>
          <div className="sm:col-span-2 flex gap-2">
            <button onClick={save} disabled={busy} data-testid={`quote-edit-save-${q.id}`}
              className={`${btnBase} bg-[#233D35] text-[#F3EFE5] hover:bg-[#1A2621] disabled:opacity-60`}>
              <Check size={14} /> {busy ? "Saving…" : "Save Changes"}
            </button>
            <button onClick={() => setEditing(false)} className={`${btnBase} border border-[#E2DACB] text-[#77786F]`} data-testid={`quote-edit-cancel-${q.id}`}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteCard;
